import { call, put, takeEvery, takeLatest } from 'redux-saga/effects'
import {
  AdminCreateLmsTraining,
  AdminDeleteLmsTraining,
  AdminFetchLmsTrainingCatalog,
  AdminUpdateLmsTraining,
} from 'src/constants/actionTypes'
import { Endpoints } from 'src/constants/endpoints'
import type { LmsTrainingPlain } from 'src/interfaces/collectStepReference'
import type {
  CreateLmsTrainingPayload,
  DeleteLmsTrainingPayload,
  UpdateLmsTrainingPayload,
} from 'src/interfaces/lmsTraining'
import { normalizeApiListPayload } from 'src/utils/normalizeApiListPayload'
import { resolveErrorMessage } from 'src/utils/sagaErrorMessage'
import { captureSentryError } from 'src/utils/sentry'
import type { AsyncAction } from 'src/utils/reduxUtils'
import { callSecureApi } from './api/callApi'

interface LmsTrainingBodyFields {
  externalId: number
  url: string
  title: string
  description?: string
  manualUploadRequirements?: string
}

/**
 * Builds the request body field by field rather than forwarding the action payload.
 *
 * Both the POST and the PUT run `whitelist + forbidNonWhitelisted`, so forwarding the payload
 * would send `lmsTrainingId` alongside the fields and 400 the whole request.
 *
 * Empty optionals are omitted rather than sent as `''`: the backend update service filters the
 * body to truthy values, so an empty string is ignored anyway — and sending one would tell the
 * admin a value was cleared when it was not. `externalId`, `url` and `title` are always
 * present, which is what guarantees the PUT never hits the 'At least one field must be
 * provided' rejection. `externalId` is copied unconditionally so a legitimate `0` survives.
 */
const buildLmsTrainingBody = (fields: LmsTrainingBodyFields): LmsTrainingBodyFields => {
  const body: LmsTrainingBodyFields = {
    externalId: fields.externalId,
    url: fields.url,
    title: fields.title,
  }

  if (fields.description) {
    body.description = fields.description
  }

  if (fields.manualUploadRequirements) {
    body.manualUploadRequirements = fields.manualUploadRequirements
  }

  return body
}

export function* onFetchLmsTrainingCatalog(action: AsyncAction<void>) {
  try {
    // Reuses the endpoint the flow editor already calls. The endpoint answers with a raw array
    // rather than `{ data, total }`; the normalizer handles both.
    const { payload }: { payload: unknown } = yield call(
      callSecureApi,
      Endpoints.AdminLmsTrainingList,
      { method: 'GET' },
    )

    const lmsTrainings = normalizeApiListPayload<LmsTrainingPlain>(payload)

    yield put(AdminFetchLmsTrainingCatalog.success(lmsTrainings))

    if (action.next) {
      action.next(null, lmsTrainings)
    }
  } catch (error: unknown) {
    captureSentryError(error, {
      message: '[saga.lmsTraining.onFetchLmsTrainingCatalog] error',
    })

    yield put(AdminFetchLmsTrainingCatalog.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to load LMS courses'))
    }
  }
}

export function* onCreateLmsTraining(action: AsyncAction<CreateLmsTrainingPayload>) {
  try {
    const { payload }: { payload: LmsTrainingPlain } = yield call(
      callSecureApi,
      Endpoints.AdminCreateLmsTraining,
      {
        method: 'POST',
        body: buildLmsTrainingBody(action.payload),
      },
    )

    yield put(AdminCreateLmsTraining.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.lmsTraining.onCreateLmsTraining] error' })

    yield put(AdminCreateLmsTraining.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to create LMS course'))
    }
  }
}

export function* onUpdateLmsTraining(action: AsyncAction<UpdateLmsTrainingPayload>) {
  const lmsTrainingId = action.payload?.lmsTrainingId

  if (!lmsTrainingId) {
    yield put(AdminUpdateLmsTraining.failed(new Error('Missing lmsTrainingId')))

    if (action.next) {
      action.next('Missing lmsTrainingId')
    }

    return
  }

  try {
    const { payload }: { payload: LmsTrainingPlain } = yield call(
      callSecureApi,
      Endpoints.AdminUpdateLmsTraining,
      {
        method: 'PUT',
        params: [{ field: ':lmsTrainingId', value: lmsTrainingId }],
        body: buildLmsTrainingBody(action.payload),
      },
    )

    yield put(AdminUpdateLmsTraining.success(payload))

    if (action.next) {
      action.next(null, payload)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.lmsTraining.onUpdateLmsTraining] error' })

    yield put(AdminUpdateLmsTraining.failed(error))

    if (action.next) {
      action.next(resolveErrorMessage(error, 'Failed to update LMS course'))
    }
  }
}

export function* onDeleteLmsTraining(action: AsyncAction<DeleteLmsTrainingPayload>) {
  const lmsTrainingId = action.payload?.lmsTrainingId

  if (!lmsTrainingId) {
    yield put(AdminDeleteLmsTraining.failed(new Error('Missing lmsTrainingId')))

    if (action.next) {
      action.next('Missing lmsTrainingId')
    }

    return
  }

  try {
    yield call(callSecureApi, Endpoints.AdminDeleteLmsTraining, {
      method: 'DELETE',
      params: [{ field: ':lmsTrainingId', value: lmsTrainingId }],
    })

    yield put(AdminDeleteLmsTraining.success(lmsTrainingId))

    if (action.next) {
      action.next(null, lmsTrainingId)
    }
  } catch (error: unknown) {
    captureSentryError(error, { message: '[saga.lmsTraining.onDeleteLmsTraining] error' })

    yield put(AdminDeleteLmsTraining.failed(error))

    if (action.next) {
      // The backend has no in-use guard. The FK is only on the per-contractor
      // `lms-training-collect-data-step` rows, so a course a contractor has already STARTED
      // fails at the database as an opaque 500 with no message — hence the fallback carries
      // the weight here. A template-only reference lives in jsonb with no FK and does NOT
      // fail: that delete succeeds and leaves the template dangling. See the design's §2.4.2.
      action.next(resolveErrorMessage(error, 'Failed to delete LMS course'))
    }
  }
}

export function* lmsTrainingSaga() {
  // A refresh or a navigation re-triggers the fetch, so only the newest run matters.
  yield takeLatest(AdminFetchLmsTrainingCatalog.type.REQUEST, onFetchLmsTrainingCatalog)
  // Mutations must each run.
  yield takeEvery(AdminCreateLmsTraining.type.REQUEST, onCreateLmsTraining)
  yield takeEvery(AdminUpdateLmsTraining.type.REQUEST, onUpdateLmsTraining)
  yield takeEvery(AdminDeleteLmsTraining.type.REQUEST, onDeleteLmsTraining)
}
